import React, { useEffect, useState, useMemo } from 'react';
import { Alert, Button, Card, Col, Form, Row } from 'react-bootstrap';
import { FaCalculator, FaPlus, FaTrash } from 'react-icons/fa';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { Table, Thead, Tbody, Tr, Th, Td } from 'react-super-responsive-table';
import Select from 'react-select';
import CurrencyInput from 'react-currency-input';
import moment from 'moment';
import MaskedInput from 'react-text-mask';
import { customStylesSm, DropdownIndicator } from '~/Styles/reactSelectStyles';
import { MASK_DATE, recorrenciaDuracoes } from '~/constant';
import { calcularValorPresente } from '~/Pages/Vendas/helpers';
import CardMobile from './CardMobile';
import { formatCurrency, formatDate } from '../utils';

var opcoesRecorrencia = [
  { value: 'anual', label: 'Anual' },
  { value: 'semestral', label: 'Semestral' },
  { value: 'quadrimestral', label: 'Quadrimestral' },
  { value: 'trimestral', label: 'Trimestral' },
  { value: 'mensal', label: 'Mensal' },
  { value: 'avulsa', label: 'Avulsa' },
];

export default function ParcelasIntermediariasManual(props) {
  var parcelas = props.parcelas || [];
  var setParcelas = props.setParcelas;
  var taxaPI = props.taxaPI || 0;
  var dataVenda = props.dataVenda;
  var bloqueado = !!props.bloqueado;
  var mensagemBloqueio = props.mensagemBloqueio || 'Seção bloqueada.';
  // Flag que altera data-base do PRICE quando há intermediárias.
  // Backend usa em ResumoVenda.cs:1019 — só impacta cálculo se existir parcela.
  var isParcelasNaoConcomitantes = !!props.isParcelasNaoConcomitantes;
  var setIsParcelasNaoConcomitantes = props.setIsParcelasNaoConcomitantes;
  var resetSignal = props.resetSignal;

  var [tipo, setTipo] = useState(opcoesRecorrencia[0]);
  var [qtd, setQtd] = useState(1);
  var [valor, setValor] = useState('');
  var [vencimento, setVencimento] = useState(
    moment().add(12, 'months').date(20).format('DD/MM/YYYY')
  );
  var [previa, setPrevia] = useState([]);

  // Reset signal do pai: limpa form e prévia quando o pai zera parcelas
  // intermediárias por algum gatilho externo.
  useEffect(function() {
    setTipo(opcoesRecorrencia[0]);
    setQtd(1);
    setValor('');
    setVencimento(moment().add(12, 'months').date(20).format('DD/MM/YYYY'));
    setPrevia([]);
  }, [resetSignal]);

  var isAvulsa = tipo.value === 'avulsa';
  // Espelha venda real (Components/Vendas/ParcelasIntermediarias:37): checkbox
  // "não concomitantes" só faz sentido quando há recorrência mensal — porque o
  // financiamento é mensal também, e o flag muda data-base do PRICE.
  var isMensal = tipo.value === 'mensal';
  var temParcelas = parcelas.length > 0;

  var selectStyles = Object.assign({}, customStylesSm, {
    menuPortal: function(base) {
      return Object.assign({}, base, { zIndex: 99999 });
    },
  });

  var handleCalcular = function() {
    var valorNum = parseFloat(valor) || 0;
    if (valorNum <= 0) return;

    var qtdParcelas = isAvulsa ? 1 : Number(qtd) || 1;
    var mesesRec = recorrenciaDuracoes[tipo.value] || 1;
    var baseVenc = moment(vencimento, 'DD/MM/YYYY');
    if (!baseVenc.isValid()) return;

    var base = dataVenda ? new Date(dataVenda) : new Date();
    var novas = [];

    for (var i = 0; i < qtdParcelas; i++) {
      var venc =
        i === 0
          ? baseVenc.clone().toDate()
          : baseVenc.clone().add(i * mesesRec, 'months').toDate();

      var vp = calcularValorPresente(valorNum, taxaPI, base, venc);

      novas.push({
        tipo: tipo.label,
        valorFuturo: valorNum,
        valorPresente: vp,
        vencimento: venc,
      });
    }

    setPrevia(novas);
  };

  var handleAdicionar = function() {
    if (previa.length === 0) return;
    var proximas = parcelas.concat(
      previa.map(function(p, idx) {
        return Object.assign({}, p, {
          id: Date.now() + '-' + idx,
        });
      })
    );
    setParcelas(proximas);
    setPrevia([]);
    setValor('');
  };

  var handleRemover = function(id) {
    setParcelas(
      parcelas.filter(function(p) {
        return p.id !== id;
      })
    );
  };

  var handleLimparTudo = function() {
    setParcelas([]);
    setPrevia([]);
  };

  var totais = useMemo(
    function() {
      var vp = 0;
      var vf = 0;
      parcelas.forEach(function(p) {
        vp += p.valorPresente || 0;
        vf += p.valorFuturo || 0;
      });
      return { vp: vp, vf: vf };
    },
    [parcelas]
  );

  var previaTotal = useMemo(
    function() {
      var vp = 0;
      var vf = 0;
      previa.forEach(function(p) {
        vp += p.valorPresente || 0;
        vf += p.valorFuturo || 0;
      });
      return { vp: vp, vf: vf };
    },
    [previa]
  );

  if (bloqueado) {
    return (
      <Card className="mb-2">
        <Card.Body className="py-2">
          <h6 className="mb-1 text-primary">
            <strong>Parcelas Intermediárias</strong>
          </h6>
          <div
            className="rounded p-2"
            style={{ background: '#fff3cd', border: '1px solid #ffc107' }}
          >
            <small>{mensagemBloqueio}</small>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="mb-2">
      <Card.Body className="py-2">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="mb-0 text-primary">
            <strong>Parcelas Intermediárias</strong>
          </h6>
          {taxaPI > 0 && (
            <small className="text-muted">
              Taxa PI: {formatCurrency(taxaPI)}% a.m.
            </small>
          )}
        </div>

        <Row>
          <Col xs={12} md={3}>
            <Form.Group className="mb-2">
              <Form.Label className="small mb-1">Recorrência</Form.Label>
              <Select
                options={opcoesRecorrencia}
                value={tipo}
                onChange={setTipo}
                styles={selectStyles}
                components={{ DropdownIndicator: DropdownIndicator }}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </Form.Group>
          </Col>
          {!isAvulsa && (
            <Col xs={6} md={2}>
              <Form.Group className="mb-2">
                <Form.Label className="small mb-1">Parcelas</Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  min="1"
                  value={qtd}
                  onChange={function(e) {
                    setQtd(e.target.value);
                  }}
                />
              </Form.Group>
            </Col>
          )}
          <Col xs={isAvulsa ? 12 : 6} md={isAvulsa ? 4 : 3}>
            <Form.Group className="mb-2">
              <Form.Label className="small mb-1">Valor</Form.Label>
              <CurrencyInput
                className="form-control form-control-sm"
                value={valor}
                decimalSeparator=","
                thousandSeparator="."
                prefix="R$ "
                onChangeEvent={function(e, masked, floatvalue) {
                  setValor(floatvalue);
                }}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={isAvulsa ? 5 : 4}>
            <Form.Group className="mb-2">
              <Form.Label className="small mb-1">1º Vencimento</Form.Label>
              <MaskedInput
                className="form-control form-control-sm"
                mask={MASK_DATE}
                value={vencimento}
                onChange={function(e) {
                  setVencimento(e.target.value);
                }}
                placeholder="DD/MM/AAAA"
              />
            </Form.Group>
          </Col>
        </Row>

        {isMensal && setIsParcelasNaoConcomitantes && (
          <Row className="mt-1 mb-2">
            <Col xs={12}>
              <Form.Check
                type="checkbox"
                id="parcelas-nao-concomitantes"
                checked={isParcelasNaoConcomitantes}
                onChange={function(e) { setIsParcelasNaoConcomitantes(e.target.checked); }}
                label={<small>Parcelas não concomitantes</small>}
              />
            </Col>
          </Row>
        )}

        <Row className="mb-2">
          <Col xs={12} className="d-flex flex-column flex-sm-row justify-content-between align-items-stretch align-items-sm-center">
            {temParcelas ? (
              <Button
                size="sm"
                variant="outline-danger"
                onClick={handleLimparTudo}
                className="mb-2 mb-sm-0"
              >
                <FaTrash className="mr-1" />
                Limpar tudo
              </Button>
            ) : <span />}
            <Button
              size="sm"
              variant="outline-primary"
              onClick={handleCalcular}
              disabled={!valor}
            >
              <FaCalculator className="mr-1" />
              Calcular
            </Button>
          </Col>
        </Row>

        {previa.length > 0 && (
          <div className="border rounded p-2 mb-2" style={{ background: '#f8f9fa' }}>
            <div className="d-flex justify-content-between align-items-center mb-1">
              <small className="text-muted">
                Prévia: {previa.length} parcela(s) — VP{' '}
                <strong>R$ {formatCurrency(previaTotal.vp)}</strong> · VF{' '}
                <strong>R$ {formatCurrency(previaTotal.vf)}</strong>
              </small>
              <Button size="sm" variant="success" onClick={handleAdicionar}>
                <FaPlus className="mr-1" />
                Adicionar
              </Button>
            </div>
          </div>
        )}

        {parcelas.length === 0 && previa.length === 0 && (
          <Alert variant="light" className="py-1 mb-0 text-center">
            <small className="text-muted">
              Nenhuma parcela intermediária adicionada. Preencha acima e
              clique em Calcular.
            </small>
          </Alert>
        )}

        {parcelas.length > 0 && (
          <>
            <div className="mb-1 mt-2">
              <small className="text-muted">
                {parcelas.length} parcela(s) adicionada(s) — VP{' '}
                <strong>R$ {formatCurrency(totais.vp)}</strong> · VF{' '}
                <strong>R$ {formatCurrency(totais.vf)}</strong>
              </small>
            </div>

            <div className="d-none d-md-block">
              <PerfectScrollbar style={{ maxHeight: 250 }} className="border">
                <Table className="table table-sm table-striped table-borderless mb-0">
                  <Thead>
                    <Tr>
                      <Th>Tipo</Th>
                      <Th className="text-right">Valor Presente</Th>
                      <Th className="text-right">Valor Futuro</Th>
                      <Th className="text-center">Vencimento</Th>
                      <Th></Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {parcelas.map(function(p) {
                      return (
                        <Tr key={p.id}>
                          <Td>{p.tipo}</Td>
                          <Td className="text-right">
                            R$ {formatCurrency(p.valorPresente)}
                          </Td>
                          <Td className="text-right">
                            R$ {formatCurrency(p.valorFuturo)}
                          </Td>
                          <Td className="text-center">
                            {formatDate(p.vencimento)}
                          </Td>
                          <Td className="text-center">
                            <Button
                              size="sm"
                              variant="link"
                              className="p-0 text-danger"
                              onClick={function() {
                                handleRemover(p.id);
                              }}
                            >
                              <FaTrash />
                            </Button>
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </PerfectScrollbar>
            </div>

            <div
              className="d-block d-md-none"
              style={{ maxHeight: 400, overflowY: 'auto' }}
            >
              {parcelas.map(function(p) {
                return (
                  <CardMobile
                    key={p.id}
                    titulo={p.tipo}
                    destaque={'R$ ' + formatCurrency(p.valorFuturo)}
                    campos={[
                      {
                        label: 'Valor Presente',
                        valor: 'R$ ' + formatCurrency(p.valorPresente),
                      },
                      {
                        label: 'Vencimento',
                        valor: formatDate(p.vencimento),
                      },
                    ]}
                  />
                );
              })}
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
}
